import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { UserPlus, RefreshCw, ChevronDown, Building2 } from "lucide-react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle
} from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { AddUserModal } from "@/components/admin/AddUserModal";
import { EditUserModal } from "@/components/admin/EditUserModal";
import { EditOrganizationModal } from "@/components/admin/EditOrganizationModal";
import { CreateOrganizationModal } from "@/components/admin/CreateOrganizationModal";
import { UsersTable } from "@/components/admin/UsersTable";
import { OrganizationTable } from "@/components/admin/OrganizationTable";
import { Billing } from "@/components/admin/Billing";
import { useAuth } from "@/providers/AuthProvider";
import { useAdminData, User, Organization } from "@/hooks/useAdminData";
import { useUserManagement } from "@/hooks/useUserManagement";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import CustomFieldsTab from "@/components/admin/CustomFieldsTab";
import KanbanColumnsTab from "@/components/admin/KanbanColumnsTab";
import ScorecardFieldsTab from "@/components/admin/scorecard/ScorecardFieldsTab";
import PublicJobsTab from "@/components/admin/PublicJobsTab";
import { GlobalSystemNotifications } from "@/components/admin/GlobalSystemNotifications";
import { Statistics } from "@/components/admin/Statistics";
import { IntelligenceActionsTab } from "@/components/admin/IntelligenceActionsTab";
import { EmailTemplatesTab } from "@/components/admin/EmailTemplatesTab";
import TransitionTemplatesTab from "@/components/admin/TransitionTemplatesTab";
import { CvTemplatesTab } from "@/components/admin/CvTemplatesTab";
import { FeatureFlagsTab } from "@/components/admin/FeatureFlagsTab";

export type { User, Organization };

const AdminConsole = () => {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
	const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
	const [isEditOrganizationModalOpen, setIsEditOrganizationModalOpen] = useState(false);
	const [isCreateOrganizationModalOpen, setIsCreateOrganizationModalOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
	const navigate = useNavigate();
	const { isSuperAdmin, isAdmin, currentOrganization } = useAuth();
	const { users, organizations, isLoading, fetchData } = useAdminData();
	const { handleCreateUser, handleEditUser } = useUserManagement(fetchData);
	const { toast } = useToast();

	// Custom handler for organization updates (works with organizations table, not companies table)
	const handleEditOrganization = async (organizationData: any) => {
		try {
			// Fetch existing settings to merge (avoid overwriting other settings fields)
			const { data: existing } = await supabase
				.from('organizations')
				.select('settings')
				.eq('id', organizationData.id)
				.maybeSingle();

			const existingSettings = (existing?.settings as Record<string, any>) || {};
			const updatedSettings = {
				...existingSettings,
				email_redirect_enabled: organizationData.email_redirect_enabled ?? false,
				email_redirect_to: organizationData.email_redirect_to || null,
			};

			const { error } = await supabase
				.from('organizations')
				.update({
					name: organizationData.name,
					default_language: organizationData.default_language,
					settings: updatedSettings,
					email_sending_enabled: organizationData.email_sending_enabled ?? false,
				})
				.eq('id', organizationData.id);

			if (error) {
				console.error("Error updating organization:", error);
				throw error;
			}

			const { error: subscriptionError } = await supabase
				.from('organization_subscription')
				.update({
					free_trial_days: organizationData.free_trial_days,
					payment_grace_days: organizationData.payment_grace_days,
					skip_subscription_check: organizationData.skip_subscription_check,
					total_nbr_cv: organizationData.total_nbr_cv ?? null,
					total_nrb_job_offer: organizationData.total_nrb_job_offer ?? null,
				})
				.eq('id', organizationData.id);

			if (subscriptionError) {
				console.error("Error updating organization subscription:", subscriptionError);
				throw subscriptionError;
			}

			toast({
				title: "Success",
				description: "Organization updated successfully",
			});

			fetchData();
			setIsEditOrganizationModalOpen(false);
			setSelectedOrganization(null);
			return true;
		} catch (error: any) {
			console.error("Error updating organization:", error);
			toast({
				title: "Error",
				description: error.message || "Failed to update organization",
				variant: "destructive",
			});
			return false;
		}
	};

	// Get active tab from URL parameter or default to "users"
	const getActiveTabFromUrl = () => {
		const pageParam = searchParams.get("page");
		if (pageParam === "user" || pageParam === "users") return "users";
		if (pageParam === "organization" || pageParam === "organizations" || pageParam === "company" || pageParam === "companies") return "companies";
		if (pageParam === "billing") return "billing";
		if (pageParam === "public-jobs") return "public-jobs";
		if (pageParam === "global-notifications") return "global-notifications";
		if (pageParam === "statistics") return "statistics";
		if (pageParam === "intelligence") return "intelligence";
		if (pageParam === "email-templates") return "email-templates";
		if (pageParam === "transition-templates") return "transition-templates";
		if (pageParam === "cv-templates") return "cv-templates";
		if (pageParam === "feature-flags") return "feature-flags";
		if (pageParam === "custom-fields") return "custom-fields";
		if (pageParam === "kanban-columns") return "kanban-columns";
		if (pageParam === "scorecard-fields") return "scorecard-fields";
		return "users"; // default
	};

	const [activeTab, setActiveTab] = useState(() => getActiveTabFromUrl());
	usePageTitle("Admin Console");

	// Update active tab when URL parameters change
	useEffect(() => {
		const newActiveTab = getActiveTabFromUrl();
		setActiveTab(newActiveTab);
	}, [searchParams]);

	// Handle tab change and update URL
	const handleTabChange = (value: string) => {
		setActiveTab(value);
		const newSearchParams = new URLSearchParams(searchParams);
		if (value === "users") {
			newSearchParams.set("page", "user");
		} else if (value === "companies") {
			newSearchParams.set("page", "organization");
		} else if (value === "billing") {
			newSearchParams.set("page", "billing");
		} else if (value === "public-jobs") {
			newSearchParams.set("page", "public-jobs");
		} else if (value === "global-notifications") {
			newSearchParams.set("page", "global-notifications");
		} else if (value === "statistics") {
			newSearchParams.set("page", "statistics");
		} else if (value === "intelligence") {
			newSearchParams.set("page", "intelligence");
		} else if (value === "email-templates") {
			newSearchParams.set("page", "email-templates");
		} else if (value === "transition-templates") {
			newSearchParams.set("page", "transition-templates");
		} else if (value === "cv-templates") {
			newSearchParams.set("page", "cv-templates");
		} else if (value === "feature-flags") {
			newSearchParams.set("page", "feature-flags");
		} else if (value === "custom-fields") {
			newSearchParams.set("page", "custom-fields");
		} else if (value === "kanban-columns") {
			newSearchParams.set("page", "kanban-columns");
		} else if (value === "scorecard-fields") {
			newSearchParams.set("page", "scorecard-fields");
		}
		setSearchParams(newSearchParams);
	};

	// Modified user creation handler to close modal on success
	const handleUserCreation = async (userData: any) => {
		const success = await handleCreateUser(userData);
		if (success)
			setIsAddUserModalOpen(false);
	};

	// Fetch data on component mount
	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleRefresh = () => {
		fetchData();
	};

	if (!isSuperAdmin && !isAdmin) {
		return (
			<div className="container mx-auto py-8">
				<Card>
					<CardHeader>
						<CardTitle>{t("admin.accessDenied")}</CardTitle>
						<CardDescription>
							{t("admin.permissionMessage")}
						</CardDescription>
					</CardHeader>
				</Card>
			</div>
		);
	}

	const generalTabs = [
		{ value: "users", label: t("admin.tabs.users") },
		{ value: "companies", label: t("admin.tabs.company") },
		...((isAdmin || isSuperAdmin) ? [{ value: "billing", label: t("admin.tabs.billing") }] : []),
	];

	const configTabs = [
		{ value: "custom-fields", label: t("admin.tabs.customFields") },
		{ value: "kanban-columns", label: t("admin.tabs.kanbanColumns", "Kanban Columns") },
		{ value: "scorecard-fields", label: t("admin.tabs.scorecardFields", "Scorecard Fields") },
		{ value: "public-jobs", label: t("admin.tabs.publicJobs", "Public Jobs") },
	];

	const templateTabs = [
		{ value: "email-templates", label: t("admin.tabs.emailTemplates", "Email Templates") },
		{ value: "transition-templates", label: t("admin.tabs.transitionTemplates", "Status Transitions") },
		{ value: "cv-templates", label: t("admin.tabs.cvTemplates", "CV Templates") },
	];

	const systemTabs = isSuperAdmin ? [
		{ value: "global-notifications", label: "Global Notifications" },
		{ value: "statistics", label: "Statistics" },
		{ value: "intelligence", label: "Intelligence" },
		{ value: "feature-flags", label: "Feature Flags" },
	] : [];

	const isInGroup = (tabs: { value: string }[]) => tabs.some(tab => tab.value === activeTab);

	const NavDropdown = ({ label, tabs }: { label: string; tabs: { value: string; label: string }[] }) => (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="sm"
					className={cn(
						"h-8 gap-1 text-sm font-medium",
						isInGroup(tabs) && "bg-accent text-accent-foreground"
					)}
				>
					{label}
					<ChevronDown className="h-3.5 w-3.5 opacity-60" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start">
				{tabs.map(tab => (
					<DropdownMenuItem
						key={tab.value}
						onClick={() => handleTabChange(tab.value)}
						className={cn(activeTab === tab.value && "bg-accent")}
					>
						{tab.label}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);

	const navMenu = (
		<div className="flex items-center gap-1">
			<NavDropdown label={t("admin.sidebar.general", "General")} tabs={generalTabs} />
			<NavDropdown label={t("admin.sidebar.configuration", "Configuration")} tabs={configTabs} />
			<NavDropdown label={t("admin.sidebar.templates", "Templates")} tabs={templateTabs} />
			{isSuperAdmin && <NavDropdown label={t("admin.sidebar.system", "System")} tabs={systemTabs} />}
		</div>
	);

	return (
		<div className="container mx-auto py-4">
			<PageHeader
				title={t("admin.console")}
				description={t("admin.description")}
				actions={navMenu}
			/>

			<div className="mt-6">
				{activeTab === "users" && (
					<>
						<div className="flex items-center justify-between mb-4">
							<div>
								<h2 className="text-xl font-semibold">{t("admin.users.title")}</h2>
								<p className="text-sm text-muted-foreground">{t("admin.users.description")}</p>
							</div>
							<div className="flex gap-2">
								<Button onClick={handleRefresh} variant="outline" size="sm">
									<RefreshCw className="mr-2 h-4 w-4" />
									{t("admin.refresh")}
								</Button>
								<Button onClick={() => setIsAddUserModalOpen(true)} size="sm" className="bg-digger-blue hover:bg-digger-blue-light">
									<UserPlus className="mr-2 h-4 w-4" />
									{t("admin.addUser")}
								</Button>
							</div>
						</div>
						{isLoading ? (
							<div className="flex justify-center py-8">
								<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-digger-blue"></div>
							</div>
						) : (
							<UsersTable
								users={users}
								isLoading={isLoading}
								onEditUser={(user) => {
									setSelectedUser(user);
									setIsEditUserModalOpen(true);
								}}
								onRefresh={handleRefresh}
							/>
						)}
					</>
				)}

				{activeTab === "companies" && (
					<>
						<div className="flex items-center justify-between mb-4">
							<div>
								<h2 className="text-xl font-semibold">{t("admin.company.title")}</h2>
								<p className="text-sm text-muted-foreground">{t("admin.company.description")}</p>
							</div>
							{isSuperAdmin && (
								<Button onClick={() => setIsCreateOrganizationModalOpen(true)} size="sm" className="bg-digger-blue hover:bg-digger-blue-light">
									<Building2 className="mr-2 h-4 w-4" />
									{t("admin.company.addOrg")}
								</Button>
							)}
						</div>
						<OrganizationTable
							organization={organizations}
							isLoading={isLoading}
							onEditCompany={(company) => {
								setSelectedOrganization(company);
								setIsEditOrganizationModalOpen(true);
							}}
						/>
					</>
				)}

				{activeTab === "billing" && (
					<>
						<div className="mb-4">
							<h2 className="text-xl font-semibold">{t("admin.billing.title")}</h2>
							<p className="text-sm text-muted-foreground">{t("admin.billing.description")}</p>
						</div>
						<Billing />
					</>
				)}

				{activeTab === "public-jobs" && <PublicJobsTab />}

				{activeTab === "custom-fields" && (
					<>
						<div className="mb-4">
							<h2 className="text-xl font-semibold">{t("admin.customFields.title")}</h2>
							<p className="text-sm text-muted-foreground">{t("admin.customFields.description")}</p>
						</div>
						<CustomFieldsTab />
					</>
				)}

				{activeTab === "kanban-columns" && (
					<>
						<div className="mb-4">
							<h2 className="text-xl font-semibold">{t("admin.kanbanColumns.title")}</h2>
							<p className="text-sm text-muted-foreground">{t("admin.kanbanColumns.description")}</p>
						</div>
						<KanbanColumnsTab />
					</>
				)}

				{activeTab === "scorecard-fields" && (
					<>
						<div className="mb-4">
							<h2 className="text-xl font-semibold">{t("admin.scorecardFields.title", "Scorecard Fields")}</h2>
							<p className="text-sm text-muted-foreground">{t("admin.scorecardFields.description", "Configure the evaluation criteria shown on the Scorecard tab of every candidate profile.")}</p>
						</div>
						<ScorecardFieldsTab />
					</>
				)}

				{activeTab === "email-templates" && <EmailTemplatesTab />}

				{activeTab === "transition-templates" && <TransitionTemplatesTab />}

				{activeTab === "cv-templates" && <CvTemplatesTab />}

				{activeTab === "global-notifications" && isSuperAdmin && <GlobalSystemNotifications />}

				{activeTab === "statistics" && isSuperAdmin && (
					<>
						<div className="mb-4">
							<h2 className="text-xl font-semibold">System Statistics</h2>
							<p className="text-sm text-muted-foreground">Technical metrics and system performance data</p>
						</div>
						<Statistics />
					</>
				)}

				{activeTab === "intelligence" && isSuperAdmin && <IntelligenceActionsTab />}

				{activeTab === "feature-flags" && isSuperAdmin && <FeatureFlagsTab />}
			</div>

			<AddUserModal
				isOpen={isAddUserModalOpen}
				onClose={() => setIsAddUserModalOpen(false)}
				onSubmit={handleUserCreation}
				organizations={organizations}
			/>

			<CreateOrganizationModal
				isOpen={isCreateOrganizationModalOpen}
				onClose={() => setIsCreateOrganizationModalOpen(false)}
				onSuccess={handleRefresh}
			/>

			{selectedUser && (
				<EditUserModal
					isOpen={isEditUserModalOpen}
					onClose={() => {
						setIsEditUserModalOpen(false);
						setSelectedUser(null);
					}}
					onSubmit={handleEditUser}
					organizations={organizations}
					user={selectedUser}
				/>
			)}

			{selectedOrganization && (
				<EditOrganizationModal
					isOpen={isEditOrganizationModalOpen}
					onClose={() => {
						setIsEditOrganizationModalOpen(false);
						setSelectedOrganization(null);
					}}
					onSubmit={handleEditOrganization}
					company={selectedOrganization}
				/>
			)}
		</div>
	);
};

export default AdminConsole;
